import React, { useState, useEffect } from 'react';
import {
  Paper,
  Typography,
  Box,
  Button,
  Table,
  TableBody,
  TableCell,
  TableContainer,
  TableHead,
  TableRow,
  Chip,
  IconButton,
  Dialog,
  DialogTitle,
  DialogContent,
  DialogContentText,
  DialogActions,
  Alert,
  CircularProgress
} from '@mui/material';
import {
  Delete as DeleteIcon,
  VpnKey as VpnKeyIcon
} from '@mui/icons-material';
import api from '../services/api';
import ResponsivePageHeader from '../components/Common/ResponsivePageHeader';
import { useAuth } from '../contexts/AuthContext';
import { logger } from '../utils/logger';

interface CompanyAPIKey {
  company_id: string;
  company_name: string;
  has_api_key: boolean;
  created_at: string;
  tier: string;
  is_active: boolean;
}

const AdminAPIKeysManagement: React.FC = () => {
  const { user } = useAuth();
  const [companies, setCompanies] = useState<CompanyAPIKey[]>([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState<string | null>(null);
  const [success, setSuccess] = useState<string | null>(null);
  const [revoking, setRevoking] = useState(false);
  const [revokeDialogOpen, setRevokeDialogOpen] = useState(false);
  const [selectedCompany, setSelectedCompany] = useState<CompanyAPIKey | null>(null);

  useEffect(() => {
    fetchCompanies();
  }, []);

  const fetchCompanies = async () => {
    try {
      const response = await api.get('/api-keys/admin/companies');
      const data = response.data;
      setCompanies(data);
    } catch (err: any) {
      logger.error('Failed to fetch companies:', err);
      setError(err.message || 'Greška pri učitavanju kompanija');
    } finally {
      setLoading(false);
    }
  };

  const handleRevokeClick = (company: CompanyAPIKey) => {
    setSelectedCompany(company);
    setRevokeDialogOpen(true);
  };

  const handleRevokeConfirm = async () => {
    if (!selectedCompany) return;

    setRevoking(true);
    setError(null);

    try {
      await api.delete(`/api-keys/admin/revoke/${selectedCompany.company_id}`);

      setSuccess(`API ključ za kompaniju "${selectedCompany.company_name}" je uspešno povučen.`);
      setRevokeDialogOpen(false);
      setSelectedCompany(null);
      // Refresh list
      fetchCompanies();
    } catch (err: any) {
      logger.error('Failed to revoke API key:', err);
      setError(err.message || 'Greška pri povlačenju API ključa');
    } finally {
      setRevoking(false);
    }
  };

  const getTierColor = (tier: string) => {
    switch (tier.toLowerCase()) {
      case 'free': return 'default';
      case 'basic': return 'primary';
      case 'premium': return 'secondary';
      case 'enterprise': return 'success';
      default: return 'default';
    }
  };

  const formatDate = (dateString: string) => {
    return new Date(dateString).toLocaleDateString('sr-RS', {
      day: 'numeric',
      month: 'long',
      year: 'numeric',
      hour: '2-digit',
      minute: '2-digit'
    });
  };

  if (loading) {
    return (
      <Box sx={{ display: 'flex', justifyContent: 'center', alignItems: 'center', minHeight: '50vh' }}>
        <CircularProgress />
      </Box>
    );
  }

  return (
    <Box sx={{
      p: 3,
      m: 0,
      minHeight: '100vh',
      width: '100%',
      background: 'linear-gradient(135deg, #f7faff 0%, #dfeafc 50%, #abc3ea 100%)',
      position: 'relative',
    }}>
      <ResponsivePageHeader
        title="API Ključevi Kompanija"
        subtitle="Pregledajte i upravljajte API ključevima svih kompanija"
        userInfo={{
          name: user?.email?.split('@')[0] || 'Admin',
          email: user?.email,
          company: user?.company_name || 'OmniPass Portal',
          role: user?.role
        }}
      />

      {success && (
        <Alert severity="success" sx={{ mb: 2 }} onClose={() => setSuccess(null)}>
          {success}
        </Alert>
      )}
      {error && (
        <Alert severity="error" sx={{ mb: 2 }} onClose={() => setError(null)}>
          {error}
        </Alert>
      )}

      <Paper sx={{ p: 3 }}>
        <Box sx={{ display: 'flex', alignItems: 'center', mb: 3 }}>
          <VpnKeyIcon sx={{ fontSize: 32, mr: 2, color: 'primary.main' }} />
          <Box>
            <Typography variant="h5" gutterBottom>
              Kompanije sa API Ključevima
            </Typography>
            <Typography variant="body2" color="text.secondary">
              Ukupno kompanija sa aktivnim API ključem: {companies.filter(c => c.has_api_key).length}
            </Typography>
          </Box>
        </Box>

        {companies.length === 0 ? (
          <Alert severity="info">
            Trenutno nijedna kompanija nema generisan API ključ.
          </Alert>
        ) : (
          <TableContainer>
            <Table>
              <TableHead>
                <TableRow>
                  <TableCell><strong>Kompanija</strong></TableCell>
                  <TableCell><strong>Tier</strong></TableCell>
                  <TableCell><strong>Kreiran</strong></TableCell>
                  <TableCell><strong>Status</strong></TableCell>
                  <TableCell align="right"><strong>Akcije</strong></TableCell>
                </TableRow>
              </TableHead>
              <TableBody>
                {companies
                  .filter(company => company.has_api_key)
                  .map((company) => (
                  <TableRow key={company.company_id}>
                    <TableCell>{company.company_name}</TableCell>
                    <TableCell>
                      <Chip
                        label={company.tier.toUpperCase()}
                        color={getTierColor(company.tier) as any}
                        size="small"
                      />
                    </TableCell>
                    <TableCell>
                      {company.created_at ? formatDate(company.created_at) : 'N/A'}
                    </TableCell>
                    <TableCell>
                      <Chip
                        label={company.is_active ? 'Aktivan' : 'Neaktivan'}
                        color={company.is_active ? 'success' : 'default'}
                        size="small"
                      />
                    </TableCell>
                    <TableCell align="right">
                      <IconButton
                        color="error"
                        onClick={() => handleRevokeClick(company)}
                        title="Povuci API ključ"
                      >
                        <DeleteIcon />
                      </IconButton>
                    </TableCell>
                  </TableRow>
                ))}
              </TableBody>
            </Table>
          </TableContainer>
        )}
      </Paper>

      {/* Revoke Confirmation Dialog */}
      <Dialog open={revokeDialogOpen} onClose={() => !revoking && setRevokeDialogOpen(false)}>
        <DialogTitle>Povuci API Ključ</DialogTitle>
        <DialogContent>
          <DialogContentText>
            Da li ste sigurni da želite da povučete API ključ za kompaniju "{selectedCompany?.company_name}"?
          </DialogContentText>
          <Alert severity="warning" sx={{ mt: 2 }}>
            <strong>Upozorenje:</strong> Ova akcija će odmah onemogućiti sve API pozive sa ovim ključem.
            Kompanija će morati da generiše novi ključ ako želi da nastavi sa korišćenjem API-ja.
          </Alert>
        </DialogContent>
        <DialogActions>
          <Button onClick={() => setRevokeDialogOpen(false)} disabled={revoking}>
            Otkaži
          </Button>
          <Button
            onClick={handleRevokeConfirm}
            color="error"
            variant="contained"
            disabled={revoking}
          >
            {revoking ? 'Povlačim...' : 'Povuci Ključ'}
          </Button>
        </DialogActions>
      </Dialog>
    </Box>
  );
};

export default AdminAPIKeysManagement;
